import { addDays, format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toISO, todayISO, fmtTime } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { jobTypeColor, textOn } from "@/lib/jobTypes";
import { cn } from "@/lib/utils";

export const TruckGrid = ({ start, days }) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const today = todayISO();
  const cols = Array.from({ length: 7 }, (_, i) => toISO(addDays(parseISO(start), i)));
  const all = cols.flatMap((iso) => days[iso]?.jobs || []);
  const trucks = [...new Set([...settings.trucks, ...all.map((j) => j.truck_assigned).filter(Boolean)])];
  const rows = [...trucks.map((t) => ({ key: t, label: t })), { key: "", label: "No truck assigned", muted: true }];
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="truck-grid">
      <div className="border-b border-slate-100 px-4 py-2.5">
        <h3 className="font-display text-sm font-semibold text-[#0E1E38]">Truck availability</h3>
        <p className="text-xs text-slate-500">Each truck across the week – empty cells are free slots. Add trucks under Settings → Lists.</p>
      </div>
      <table className="w-full min-w-[820px] text-xs">
        <thead>
          <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <th className="w-40 px-3 py-2">Truck</th>
            {cols.map((iso) => <th key={iso} className={cn("px-2 py-2", iso === today && "text-[#0E1E38]")}>{format(parseISO(iso), "EEE d")}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key || "none"} className="border-t border-slate-100" data-testid={`truck-row-${r.key || "none"}`}>
              <td className={cn("px-3 py-2 font-semibold", r.muted ? "text-slate-400" : "text-slate-800")}>{r.label}</td>
              {cols.map((iso) => {
                const jobs = (days[iso]?.jobs || []).filter((j) => (j.truck_assigned || "") === r.key);
                return (
                  <td key={iso} className={cn("min-w-[100px] px-1.5 py-1.5 align-top", iso === today && "bg-amber-50/40")} data-testid={`truck-cell-${r.key || "none"}-${iso}`}>
                    {jobs.length === 0 ? (
                      r.muted ? null : <span className="block rounded border border-dashed border-emerald-200 px-1.5 py-1 text-center text-[10px] font-medium text-emerald-600">Free</span>
                    ) : jobs.map((j) => {
                      const c = jobTypeColor(settings.job_types, j.job_type);
                      return (
                        <button key={j.id} onClick={() => navigate(`/bookings/${j.id}`)} className="mb-1 block w-full truncate rounded px-1.5 py-1 text-left text-[10px] leading-tight hover:opacity-85" style={{ background: c, color: textOn(c) }} title={`${j.job_number} ${j.customer_name} · ${j.job_type || ""} · ${fmtTime(j.start_time)}`} data-testid={`truck-job-${j.id}`}>
                          <span className="font-mono font-bold">{fmtTime(j.start_time)}</span> {j.job_type || j.customer_name}
                        </button>
                      );
                    })}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
